
import { supabase } from "@/integrations/supabase/client";
import { RealtimeChannel } from "@supabase/supabase-js";

type ChannelRef = {
  channel: RealtimeChannel;
  refCount: number;
  isSubscribed: boolean;
  lastActivity: number; // Timestamp of last activity
  pendingSubscription: boolean; // Flag to track if subscription is in progress
};

// Global channel tracking
const channelRefs = new Map<string, ChannelRef>();

// Debounce period in milliseconds
const DEBOUNCE_PERIOD = 2000;

// Cleanup interval in milliseconds
const CLEANUP_INTERVAL = 60000; // 1 minute

// Set up periodic cleanup of stale channels
setInterval(() => {
  const now = Date.now();
  const staleChannels: string[] = [];
  
  channelRefs.forEach((ref, channelName) => {
    // If channel has been inactive for more than 5 minutes and has no references
    if (ref.refCount === 0 && now - ref.lastActivity > 300000) {
      staleChannels.push(channelName);
    }
  });
  
  staleChannels.forEach(channelName => {
    const ref = channelRefs.get(channelName);
    if (ref) {
      console.log(`[presenceManager] Cleaning up stale channel: ${channelName}`);
      ref.channel.unsubscribe();
      channelRefs.delete(channelName);
    }
  });
}, CLEANUP_INTERVAL);

export const presenceManager = {
  getChannel(userId: string) {
    if (!userId) {
      console.error('[presenceManager] Attempted to get channel with empty userId');
      throw new Error('userId is required');
    }
    
    const channelName = `presence:user:${userId}`;
    const existing = channelRefs.get(channelName);
    const now = Date.now();
    
    if (existing) {
      // Update last activity timestamp
      existing.lastActivity = now;
      
      // If the last getChannel call was very recent, don't increment refCount to avoid rapid cycles
      const timeSinceLastActivity = now - existing.lastActivity;
      if (timeSinceLastActivity < DEBOUNCE_PERIOD) {
        console.log(`[presenceManager] Debouncing channel request for ${channelName}, time since last activity: ${timeSinceLastActivity}ms`);
        return existing.channel;
      }
      
      console.log(`[presenceManager] Reusing existing channel for ${channelName}, refCount: ${existing.refCount} -> ${existing.refCount + 1}`);
      existing.refCount++;
      return existing.channel;
    }
    
    console.log(`[presenceManager] Creating new channel for ${channelName}`);
    try {
      const channel = supabase.channel(channelName);
      channelRefs.set(channelName, { 
        channel, 
        refCount: 1,
        isSubscribed: false,
        lastActivity: now,
        pendingSubscription: false
      });
      return channel;
    } catch (error) {
      console.error(`[presenceManager] Error creating channel for ${channelName}:`, error);
      throw error;
    }
  },
  
  releaseChannel(userId: string) {
    if (!userId) {
      console.error('[presenceManager] Attempted to release channel with empty userId');
      return;
    }
    
    const channelName = `presence:user:${userId}`;
    const ref = channelRefs.get(channelName);
    const now = Date.now();
    
    if (ref) {
      // Update last activity timestamp
      ref.lastActivity = now;
      
      // If the last releaseChannel call was very recent, don't decrement refCount to avoid rapid cycles
      const timeSinceLastActivity = now - ref.lastActivity;
      if (timeSinceLastActivity < DEBOUNCE_PERIOD) {
        console.log(`[presenceManager] Debouncing release request for ${channelName}, time since last activity: ${timeSinceLastActivity}ms`);
        return;
      }
      
      ref.refCount--;
      console.log(`[presenceManager] Releasing channel for ${channelName}, refCount: ${ref.refCount + 1} -> ${ref.refCount}`);
      
      if (ref.refCount === 0) {
        // Don't immediately unsubscribe, let the cleanup interval handle it
        // This prevents rapid subscribe/unsubscribe cycles
        console.log(`[presenceManager] Marking channel ${channelName} for potential cleanup`);
      }
    }
  },

  isChannelSubscribed(userId: string): boolean {
    if (!userId) return false;
    
    const channelName = `presence:user:${userId}`;
    const ref = channelRefs.get(channelName);
    return ref ? ref.isSubscribed : false;
  },

  setChannelSubscribed(userId: string, subscribed: boolean): void {
    if (!userId) return;
    
    const channelName = `presence:user:${userId}`;
    const ref = channelRefs.get(channelName);
    if (ref) {
      console.log(`[presenceManager] Setting channel ${channelName} subscription status: ${subscribed}`);
      ref.isSubscribed = subscribed;
      ref.pendingSubscription = false;
      ref.lastActivity = Date.now();
    }
  },
  
  setPendingSubscription(userId: string, pending: boolean): void {
    if (!userId) return;
    
    const channelName = `presence:user:${userId}`;
    const ref = channelRefs.get(channelName);
    if (ref) {
      ref.pendingSubscription = pending;
      ref.lastActivity = Date.now();
    }
  },
  
  isPendingSubscription(userId: string): boolean {
    if (!userId) return false;
    
    const channelName = `presence:user:${userId}`;
    const ref = channelRefs.get(channelName);
    return ref ? ref.pendingSubscription : false;
  },
  
  getDebugInfo(): { 
    channelCount: number, 
    channelDetails: Record<string, { 
      refCount: number, 
      isSubscribed: boolean, 
      pendingSubscription: boolean,
      lastActivity: number 
    }> 
  } {
    const channelDetails: Record<string, { 
      refCount: number, 
      isSubscribed: boolean,
      pendingSubscription: boolean,
      lastActivity: number 
    }> = {};
    
    channelRefs.forEach((ref, channel) => {
      channelDetails[channel] = {
        refCount: ref.refCount,
        isSubscribed: ref.isSubscribed,
        pendingSubscription: ref.pendingSubscription,
        lastActivity: ref.lastActivity
      };
    });
    
    return {
      channelCount: channelRefs.size,
      channelDetails
    };
  }
};
